// DocTemplatesExplorerSidebar — the Documents templates tab's binding of
// the shared ExplorerSidebar. Supplies the document leaf icon and the
// template action vocabulary; all tree rendering, selection states, the
// count badge, and chrome live in ExplorerSidebar.

import {
  ClipboardPaste,
  Copy,
  FileText,
  FolderInput,
  FolderPlus,
  Pencil,
  Scissors,
  Trash2,
  Undo2,
  UploadCloud
} from 'lucide-react'
import type { ReactNode } from 'react'
import {
  isDemoMode,
  type DocTemplate,
  type DocTemplateFolder
} from '@axilon-apps/domains-ontology-ui-state'

import {
  explorerModShortcut,
  ExplorerSidebar,
  type ExplorerClipboardEntry,
  type ExplorerHeaderButton,
  type ExplorerMenuEntry,
  type ExplorerSelection,
  type ExplorerTreeNode
} from '../../components/explorer'
import { DOCS_EXPLORER_OPEN_KEY } from './explorer-paths'

export type DocTemplateItem = DocTemplate

export type DocTemplatesSidebarAction =
  | { kind: 'upload'; parentPath: string }
  | { kind: 'create-folder'; parentPath: string }
  | { kind: 'rename-folder'; path: string; name: string; folder?: DocTemplateFolder }
  | { kind: 'delete-folder'; path: string; name: string; folder?: DocTemplateFolder }
  | { kind: 'move-folder'; path: string; name: string }
  | { kind: 'move-item'; item: DocTemplateItem }
  | { kind: 'drop-folder'; fromPath: string; toParentPath: string }
  | { kind: 'drop-item'; item: DocTemplateItem; toParentPath: string }
  | { kind: 'copy-folder'; path: string; name: string }
  | { kind: 'cut-folder'; path: string; name: string }
  | { kind: 'copy-item'; item: DocTemplateItem }
  | { kind: 'cut-item'; item: DocTemplateItem }
  | { kind: 'paste'; parentPath: string }
  | { kind: 'undo' }
  | { kind: 'rename-item'; item: DocTemplateItem }
  | { kind: 'delete-item'; item: DocTemplateItem }

export interface DocTemplatesExplorerSidebarProps {
  folders: DocTemplateFolder[]
  items: DocTemplateItem[]
  selectedId: string | null
  selectedFolder: string | null
  expanded: Set<string>
  onToggle: (path: string) => void
  onSelectItem: (id: string) => void
  onSelectFolder: (path: string) => void
  onAction: (action: DocTemplatesSidebarAction) => void
  filter: string
  onFilterChange: (filter: string) => void
  isLoading?: boolean
  /** Rendered above the header — hosts the Documents/Templates toggle. */
  topSlot?: ReactNode
  /**
   * Parent path for header New folder / Upload. The current folder when a
   * folder is selected, otherwise the parent of the open item.
   */
  createParentPath?: string
  clipboard?: ExplorerClipboardEntry | null
  undoLabel?: string | null
}

const toExplorerSelection = (
  selectedId: string | null,
  selectedFolder: string | null
): ExplorerSelection => {
  if (selectedId !== null) return { kind: 'item', id: selectedId }
  if (selectedFolder !== null) return { kind: 'folder', path: selectedFolder }
  return { kind: 'none' }
}

const DocTemplatesExplorerSidebar = ({
  folders,
  items,
  selectedId,
  selectedFolder,
  expanded,
  onToggle,
  onSelectItem,
  onSelectFolder,
  onAction,
  filter,
  onFilterChange,
  isLoading,
  topSlot,
  createParentPath = '',
  clipboard = null,
  undoLabel = null
}: DocTemplatesExplorerSidebarProps) => {
  const heading = 'Templates'
  const demo = isDemoMode()
  const canUpload = !demo
  const canCreate = !demo
  const canMutate = !demo
  const canPaste = canMutate && clipboard !== null
  const copyShortcut = explorerModShortcut('C')
  const cutShortcut = explorerModShortcut('X')
  const pasteShortcut = explorerModShortcut('V')
  const undoShortcut = explorerModShortcut('Z')
  const dimmedFolderPaths =
    clipboard?.mode === 'cut' && clipboard.kind === 'folder' ? new Set([clipboard.path]) : undefined
  const dimmedItemIds =
    clipboard?.mode === 'cut' && clipboard.kind === 'item' ? new Set([clipboard.id]) : undefined

  const folderMenu = (
    node: ExplorerTreeNode<DocTemplateFolder, DocTemplateItem>
  ): ExplorerMenuEntry[] => {
    const entries: ExplorerMenuEntry[] = []
    if (canUpload) {
      entries.push({
        icon: UploadCloud,
        label: 'Upload template here',
        onSelect: () => onAction({ kind: 'upload', parentPath: node.path })
      })
    }
    if (canCreate) {
      entries.push({
        icon: FolderPlus,
        label: 'New folder here',
        onSelect: () => onAction({ kind: 'create-folder', parentPath: node.path })
      })
    }
    const displayName = node.folder?.name ?? node.name
    entries.push({
      icon: Pencil,
      label: 'Rename',
      separatorBefore: true,
      onSelect: () =>
        onAction({
          kind: 'rename-folder',
          path: node.path,
          name: displayName,
          folder: node.folder
        })
    })
    if (canMutate) {
      entries.push(
        {
          icon: FolderInput,
          label: 'Move to…',
          onSelect: () => onAction({ kind: 'move-folder', path: node.path, name: displayName })
        },
        {
          icon: Copy,
          label: 'Copy',
          shortcut: copyShortcut,
          onSelect: () => onAction({ kind: 'copy-folder', path: node.path, name: displayName })
        },
        {
          icon: Scissors,
          label: 'Cut',
          shortcut: cutShortcut,
          onSelect: () => onAction({ kind: 'cut-folder', path: node.path, name: displayName })
        },
        {
          icon: ClipboardPaste,
          label: 'Paste',
          shortcut: pasteShortcut,
          disabled: !canPaste,
          onSelect: () => onAction({ kind: 'paste', parentPath: node.path })
        }
      )
    }
    entries.push({
      icon: Trash2,
      label: 'Delete',
      variant: 'destructive',
      separatorBefore: true,
      onSelect: () =>
        onAction({
          kind: 'delete-folder',
          path: node.path,
          name: displayName,
          folder: node.folder
        })
    })
    return entries
  }

  const headerButtons: ExplorerHeaderButton[] = canCreate
    ? [
        {
          icon: FolderPlus,
          label: 'New folder',
          onClick: () => onAction({ kind: 'create-folder', parentPath: createParentPath })
        }
      ]
    : []
  if (canUpload) {
    headerButtons.push({
      icon: UploadCloud,
      label: 'Upload template',
      onClick: () => onAction({ kind: 'upload', parentPath: createParentPath })
    })
  }

  const rootMenu: ExplorerMenuEntry[] = []
  if (canUpload) {
    rootMenu.push({
      icon: UploadCloud,
      label: 'Upload template',
      onSelect: () => onAction({ kind: 'upload', parentPath: '' })
    })
  }
  if (canCreate) {
    rootMenu.push({
      icon: FolderPlus,
      label: 'New folder',
      onSelect: () => onAction({ kind: 'create-folder', parentPath: '' })
    })
  }
  if (canMutate) {
    rootMenu.push(
      {
        icon: ClipboardPaste,
        label: 'Paste',
        shortcut: pasteShortcut,
        disabled: !canPaste,
        onSelect: () => onAction({ kind: 'paste', parentPath: '' })
      },
      {
        icon: Undo2,
        label: undoLabel ?? 'Undo',
        shortcut: undoShortcut,
        disabled: undoLabel === null,
        onSelect: () => onAction({ kind: 'undo' })
      }
    )
  }

  const emptyHint = canUpload
    ? `No ${heading.toLowerCase()} yet. Right-click to upload or add a folder.`
    : `Templates are browse-only in this guided demo.`

  const itemMenu = (item: DocTemplateItem): ExplorerMenuEntry[] => {
    const entries: ExplorerMenuEntry[] = [
      {
        icon: Pencil,
        label: 'Rename',
        onSelect: () => onAction({ kind: 'rename-item', item })
      }
    ]
    if (canMutate) {
      entries.push(
        {
          icon: FolderInput,
          label: 'Move to…',
          onSelect: () => onAction({ kind: 'move-item', item })
        },
        {
          icon: Copy,
          label: 'Copy',
          shortcut: copyShortcut,
          onSelect: () => onAction({ kind: 'copy-item', item })
        },
        {
          icon: Scissors,
          label: 'Cut',
          shortcut: cutShortcut,
          onSelect: () => onAction({ kind: 'cut-item', item })
        }
      )
    }
    entries.push({
      icon: Trash2,
      label: 'Delete',
      variant: 'destructive',
      separatorBefore: true,
      onSelect: () => onAction({ kind: 'delete-item', item })
    })
    return entries
  }

  return (
    <ExplorerSidebar<DocTemplateFolder, DocTemplateItem>
      heading={heading}
      ariaLabel={`${heading} explorer`}
      folders={folders}
      items={items}
      itemIcon={FileText}
      testIdPrefix="doc-templates-tree"
      selection={toExplorerSelection(selectedId, selectedFolder)}
      expanded={expanded}
      filter={filter}
      isLoading={isLoading}
      topSlot={topSlot}
      emptyHint={emptyHint}
      headerButtons={headerButtons}
      folderMenu={folderMenu}
      itemMenu={itemMenu}
      rootMenu={rootMenu}
      onToggle={onToggle}
      onSelectFolder={onSelectFolder}
      onSelectItem={onSelectItem}
      onFilterChange={onFilterChange}
      onMoveItem={
        canMutate
          ? (item, toParentPath) => onAction({ kind: 'drop-item', item, toParentPath })
          : undefined
      }
      onMoveFolder={
        canMutate
          ? (fromPath, toParentPath) => onAction({ kind: 'drop-folder', fromPath, toParentPath })
          : undefined
      }
      dimmedFolderPaths={dimmedFolderPaths}
      dimmedItemIds={dimmedItemIds}
      collapseStorageKey={DOCS_EXPLORER_OPEN_KEY}
    />
  )
}

export default DocTemplatesExplorerSidebar
